import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { initialValues, drawerSections } from './content';
import { useLazyQuery, useMutation } from '@apollo/client';
import { cloneDeep, isEmpty } from 'lodash';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { ISalesForceBusinessPartnerDrawerProps } from '.';
import {
  getListForm,
  checkBusinessPartnerByPerson,
  createBusinessPartner,
  updateBusinessPartne,
  getActivePersonsQuery,
} from './queries';
import { extractPersons, toLookups } from './utils';
import { getError } from '@utils/graph-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { useNavigate } from 'react-router-dom';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IPagination } from '@models';

const SalesForceBusinessPartnerDrawer: React.FC<
  ISalesForceBusinessPartnerDrawerProps
> = ({
  open,
  onSuccess,
  onClose,
  salesForceBusinessPartnerDetailsInfo,
  businessPartnerId,
}) => {
  const [booted, setBooted] = useState<boolean>(false);
  const navigate = useNavigate();
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [businessPartnersDetailsListResults] = useLazyQuery(getListForm());

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [values, setValues] = useState<Record<string, any>>();

  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    drawerSections(businessPartnerId, values, lovs)
  );

  const [checkBusinessPartnerByPersonLazy] = useLazyQuery(
    checkBusinessPartnerByPerson(),
    {}
  );
  const [salesForceBusinessPartnerAction] = useMutation(
    businessPartnerId ? updateBusinessPartne() : createBusinessPartner()
  );
  const [getActivePersonsLazy, { loading }] = useLazyQuery(
    getActivePersonsQuery
  );

  const getLovs = async () => {
    const data = await businessPartnersDetailsListResults({});
    const newAssignPolicyCoverEnums = toLookups(data.data);
    const result: Record<string, Record<string, string>> = {
      SalesforceManagement_BusinessPartnerStatuses:
        newAssignPolicyCoverEnums[
          'SalesforceManagement_BusinessPartnerStatuses'
        ],
      type: newAssignPolicyCoverEnums['type'],
      category: newAssignPolicyCoverEnums['category'],
      maxCreditLimitAllowedCurrency:
        newAssignPolicyCoverEnums['maxCreditLimitAllowedCurrency'],
      person: {},
    };

    return result;
  };

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      const newLovs = await getLovs();
      const newValues = salesForceBusinessPartnerDetailsInfo || initialValues;
      setLovs(newLovs);
      setValues(newValues);
      setInputsForm(drawerSections(businessPartnerId, newValues, newLovs));
      setBooted(true);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const validateBusinessPartnerExistsOnPersonChange = async (
    selectedPersonID: string
  ) => {
    try {
      if (!isEmpty(selectedPersonID)) {
        const result = await checkBusinessPartnerByPersonLazy({
          variables: {
            selectedPersonID: selectedPersonID,
          },
        });

        if (
          result.data.SalesforceManagement?.queries
            ?.checkBusinessPartnerByPerson.length > 0
        ) {
          return `Business Partner already created for 
        ${result.data.SalesforceManagement?.queries?.checkBusinessPartnerByPerson[0]?.salesforceManagement_BusinessPartner_PersonID?.views?.SalesforceManagement_all?.properties?.PersonCode}
         - 
        ${result.data.SalesforceManagement?.queries?.checkBusinessPartnerByPerson[0]?.salesforceManagement_BusinessPartner_PersonID?.views?.SalesforceManagement_all?.properties?.FullName}`;
        }
      }
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }

    return '';
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(
      drawerSections(businessPartnerId, undefined, lovs),
      values
    );

    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      let variablesMutation = {};
      if (!businessPartnerId) {
        variablesMutation = {
          businessPartnerInputs: {
            personID: data.person,
            relatedCompany: data.relatedCompany,
            type: data.type,
            category: data.category || null,
            isCompany: data.isCompany ? data.isCompany : false,
            isLocal: data.isLocal ? data.isLocal : false,
            allowAccessToAllInsured: data.allowAccessToAllInsured || false,
            isRegistered: data.isRegistered ? data.isRegistered : false,
            registerNumber: data.registerNumber || null,
            taxAccount: data.taxAccount || null,
            account: data.account || null,
            maxCreditLimitAllowedCurrency:
              data.maxCreditLimitAllowedCurrency || null,
            maxCreditLimitAllowed: parseInt(
              data.maxCreditLimitAllowed as unknown as string
            ),
            status: data.SalesforceManagement_BusinessPartnerStatuses,
          },
        };
      } else {
        variablesMutation = {
          businessPartnerUpdateInputs: {
            relatedCompany: data.relatedCompany,
            type: data.type,
            category: data.category || null,
            isCompany: data.isCompany ? data.isCompany : false,
            isLocal: data.isLocal ? data.isLocal : false,
            allowAccessToAllInsured: data.allowAccessToAllInsured || false,
            isRegistered: data.isRegistered ? data.isRegistered : false,
            registerNumber: data.registerNumber || null,
            taxAccount: data.taxAccount || null,
            account: data.account || null,
          },
        };
      }

      const result = await salesForceBusinessPartnerAction({
        variables: businessPartnerId
          ? {
              ...variablesMutation,
              entityId: businessPartnerId,
            }
          : variablesMutation,
        errorPolicy: 'all',
      });
      if (isEmpty(result.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {businessPartnerId
              ? 'Business Partner successfully updated'
              : 'Business Partner successfully created'}
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          setSubmitButtonState('success');
          onSuccess();
          onClose();
          if (!businessPartnerId) {
            const newBusinessPartnerId =
              result.data.salesforceManagement.actions.createBusinessPartner.id;
            navigate(`/salesforce/business-partners/` + newBusinessPartnerId);
          }
        }, 500);
      } else {
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(result)}</ToastErrorMessage>);
      }
    } catch {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setFormDisabled(false);
    }
  };

  const onChange = async (
    fieldName: string,
    value: any,
    sectionIndex: number,
    values: Record<string, any>,
    errors: Record<string, string>,
    touched: Record<string, boolean>
  ) => {
    const newSections = cloneDeep(inputsForm);
    const newInputs = newSections[sectionIndex].inputs;

    newInputs[fieldName].value = value;

    switch (fieldName) {
      case 'person':
        errors.person =
          (await validateBusinessPartnerExistsOnPersonChange(value)) || '';
        touched.person = true;
        break;
      case 'type':
        if (value?.toLowerCase() === 'reinsurer') {
          newInputs.isCompany.value = true;
          newInputs.isRegistered.value = true;
          newInputs.allowAccessToAllInsured.value = false;
          newInputs.maxCreditLimitAllowedCurrency.value = null;
          newInputs.maxCreditLimitAllowed.value = null;
        } else {
          newInputs.isCompany.value = false;
          newInputs.isRegistered.value = false;
          newInputs.maxCreditLimitAllowedCurrency.value = '39735';
          newInputs.maxCreditLimitAllowed.value = 0;
        }
        break;
      default:
        break;
    }
    setValues(values);
    setInputsForm(newSections);
  };

  const handleSearch = async (
    fieldName: string,
    value: string,
    pagination: IPagination
  ) => {
    if (fieldName === 'person') {
      return getActivePersonsLazy({
        variables: {
          pagination,
          keywordSearch: value,
        },
      }).then((result) => {
        const extractedPersons = extractPersons(result.data);
        return {
          options: extractedPersons,
          totalCount:
            result.data.SalesforceManagement.queries.getPersonList.paging
              .totalCount,
        };
      });
    }

    return new Promise<{
      options: Record<string, string>;
      totalCount: number;
    }>((resolve) => {
      resolve({ options: {}, totalCount: 0 });
    });
  };

  return (
    <DynamicFormDrawer
      title={
        businessPartnerId ? 'Modify Business Partner' : 'New Business Partner'
      }
      onClose={() => onClose()}
      isOpen={open}
      loading={!booted}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      loadingFields={{
        person: loading,
      }}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      onChange={onChange}
      onAutocompleteSearch={handleSearch}
    />
  );
};

export default SalesForceBusinessPartnerDrawer;
